import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import * as deskingSelectors from '@makemydeal/dr-dash-store/selectors/deskingSelectors';
import * as unsavedChangesSelectors from '@makemydeal/dr-dash-store/selectors/unsavedChangesSelectors';
import * as offerInfoSelectors from '@makemydeal/dr-dash-store/selectors/offerInfoSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';

import { Snackbar } from '@interstate/components/Snackbar';

import { UnsavedChangesDialog } from './UnsavedChangesDialog';
import { getTimeout } from './unsavedChangesUtils';
import ReloadModal from '../reloadModal/ReloadModal';

const defaultTimeout = 10 * 60 * 1000; // 10 minutes

const BEFORE_UNLOAD_EVENT = 'beforeunload';
const USER_EVENTS = [
    'mousemove',
    'keydown',
    'wheel',
    'DOMMouseScroll',
    'mousewheel',
    'mousedown',
    'touchstart',
    'touchmove',
    'MSPointerDown',
    'MSPointerMove',
    'visibilitychange'
];

export type UnsavedChangesManagerProps = {
    /** Timeout duration in seconds after which the Unsaved Changes Dialog will appear */
    timeoutProp?: number;
};

type ReloadModalState = {
    show: boolean;
    source: string;
    message: string;
};

const INITIAL_RELOAD_MODAL_STATE: ReloadModalState = {
    show: false,
    source: '',
    message: ''
};

type AutoSaveSource = 'idle' | 'browserClose' | null;

const AUTO_SAVE_EVENT_PREFIX: Record<NonNullable<AutoSaveSource>, string> = {
    idle: 'MV:Auto-Save Draft',
    browserClose: 'MV:Auto-Save Draft On Browser Close'
};

const AUTO_SAVE_ACTION_PREFIX: Record<NonNullable<AutoSaveSource>, string> = {
    idle: 'auto_save',
    browserClose: 'auto_save_browser_close'
};

// Reusable toggler for boolean state updates
// istanbul ignore next
const toggler = (prev: boolean) => !prev;

export const UnsavedChangesManager = ({ timeoutProp = defaultTimeout }: UnsavedChangesManagerProps) => {
    const timeout = /* istanbul ignore next */ getTimeout() || timeoutProp; // first branch covered by integration tests
    const store = useStore();
    const dispatch = useDispatch();
    const timerRef = useRef(null as any);

    // Unsaved Changes dialog
    const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);

    // Reload modal state (similar to UpdatedDealBanner pattern)
    const [reloadModal, setReloadModal] = useState<ReloadModalState>(INITIAL_RELOAD_MODAL_STATE);
    const showReloadModalRef = useRef(false); // Ref to track reload modal state for beforeunload handler
    const isPageUnloadingRef = useRef(false); // Ref to track if page is in the process of unloading

    // callback not executed by this component, ignore coverage
    // istanbul ignore next
    const handleHide = useCallback(() => setShowUnsavedChangesDialog(false), []);

    // Manages the Snackbar visibility
    const [showUpdateSuccessSnackbar, setShowUpdateSuccessSnackbar] = useState(false);
    const [showDraftSuccessSnackbar, setShowDraftSuccessSnackbar] = useState(false);

    // some of these behaviors are passed as callbacks, coverage provided by UI integration tests
    // istanbul ignore next
    const toggleUpdateSuccessSnackbar = () => setShowUpdateSuccessSnackbar(toggler);
    // istanbul ignore next
    const toggleDraftSuccessSnackbar = () => setShowDraftSuccessSnackbar(toggler);

    const hasForceReloadOnWSEventTriggered = useSelector(deskingSelectors.hasForceReloadOnWSEventTriggered);
    const forceReloadSource = useSelector(deskingSelectors.getForceReloadSource);
    const forceReloadMessage = useSelector(deskingSelectors.getForceReloadMessage);
    const isDraftBeingSaved = useSelector(offerInfoSelectors.getIsDraftBeingSaved);
    const hasDraftBeenSaved = useSelector(offerInfoSelectors.getHasDraftBeenSaved);
    const dealXgId = useSelector(offerReduxSelectors.getDealXgId);
    const pendingNavigation = useSelector(baseDealSelectors.getPendingNavigation);
    const autoSaveAttemptedRef = useRef<AutoSaveSource>(null);

    const performAutoSave = useCallback(() => {
        autoSaveAttemptedRef.current = 'idle';
        logNewRelicPageAction('MV:Auto-Save Draft - Attempt', {
            action: 'auto_save_attempt',
            timestamp: Date.now(),
            timeout: timeout,
            dealId: dealXgId
        });
        dispatch(offerActionCreators.saveOffer({ isDraft: true }));
    }, [dispatch, timeout, dealXgId]);

    const handleTimeoutAction = useCallback(() => {
        const state: any = store.getState();
        const hasUnsavedChanges = unsavedChangesSelectors.currentOfferHasUnsavedChanges(state);
        if (hasUnsavedChanges && !hasForceReloadOnWSEventTriggered) {
            performAutoSave();
        }
    }, [store, hasForceReloadOnWSEventTriggered, performAutoSave]);

    useEffect(() => {
        const autoSaveSource = autoSaveAttemptedRef.current;
        if (autoSaveSource && isDraftBeingSaved === false) {
            const eventPrefix = AUTO_SAVE_EVENT_PREFIX[autoSaveSource];
            const actionPrefix = AUTO_SAVE_ACTION_PREFIX[autoSaveSource];
            if (hasDraftBeenSaved === true) {
                logNewRelicPageAction(`${eventPrefix} - Success`, {
                    action: `${actionPrefix}_success`,
                    timestamp: Date.now(),
                    result: 'success',
                    dealId: dealXgId
                });
                if (autoSaveSource === 'idle') {
                    setShowDraftSuccessSnackbar(true);
                }
            } else {
                logNewRelicPageAction(`${eventPrefix} - Failure`, {
                    action: `${actionPrefix}_failure`,
                    timestamp: Date.now(),
                    result: 'failure',
                    errorCategory: 'save_operation_failed',
                    dealId: dealXgId
                });
            }
            autoSaveAttemptedRef.current = null;
        }
    }, [isDraftBeingSaved, hasDraftBeenSaved, dealXgId]);

    // Timer management
    // For the hardcoded list of DOM events, restart the timer
    useEffect(() => {
        const restartTimer = () => {
            clearTimeout(timerRef.current);
            timerRef.current = setTimeout(handleTimeoutAction, timeout);
        };
        USER_EVENTS.forEach((event) => {
            addEventListener(event, restartTimer);
        });
        return () => {
            USER_EVENTS.forEach((event) => {
                removeEventListener(event, restartTimer);
            });
            clearTimeout(timerRef.current);
        };
    }, [handleTimeoutAction, timeout]);

    // Use the state changes of the dialog visibility to manage the timer
    useEffect(() => {
        // If the dialog visibility changes, clear the timer
        clearTimeout(timerRef.current);
        if (!showUnsavedChangesDialog) {
            // If the dialog is dismissed, start the timer
            timerRef.current = setTimeout(handleTimeoutAction, timeout);
        }
        return () => {
            clearTimeout(timerRef.current);
        };
    }, [showUnsavedChangesDialog, handleTimeoutAction, timeout]);

    // Manage beforeunload event. Purposely creating the callback once, so add/remove event listeners
    // refer to the same function in memory, so exempting the exhaustive deps rule for the hook and effect
    const beforeUnloadHandler = useCallback((event: BeforeUnloadEvent) => {
        // If reload modal is showing, don't show the unsaved changes dialog - allow reload
        if (showReloadModalRef.current) {
            return;
        }
        const state: any = store.getState();
        const hasUnsavedChanges = unsavedChangesSelectors.currentOfferHasUnsavedChanges(state);
        // Check if force reload has been triggered - if so, don't show the dialog
        const forceReloadTriggered = deskingSelectors.hasForceReloadOnWSEventTriggered(state);

        if (hasUnsavedChanges && !forceReloadTriggered) {
            // Auto-save on browser close: save draft immediately
            autoSaveAttemptedRef.current ??= 'browserClose';
            logNewRelicPageAction('MV:Auto-Save Draft On Browser Close - Attempt', {
                action: 'auto_save_browser_close_attempt',
                timestamp: Date.now(),
                dealId: offerReduxSelectors.getDealXgId(state)
            });
            dispatch(offerActionCreators.saveOffer({ isDraft: true }));

            // Show browser's native dialog
            event.preventDefault();
            event.returnValue = ''; // This triggers the browser's native "Reload site?" dialog

            // Mark that we're in the beforeunload process - don't show dialog yet
            isPageUnloadingRef.current = true;

            return ''; // For older browsers
        }
        return undefined;
    }, []);

    // Disarm the unload guard before the dialog navigates away from an unsaved draft quote, so neither the
    // auto-save nor the browser's native prompt fires - discarding the changes is what was asked for (US2063641).
    const handleLeaveWithoutSaving = useCallback(() => {
        removeEventListener(BEFORE_UNLOAD_EVENT, beforeUnloadHandler);
    }, [beforeUnloadHandler]);

    // Listen for page visibility changes to detect when user stays on page after browser dialog
    useEffect(() => {
        let stayCheckTimeoutId: ReturnType<typeof setTimeout> | null = null;

        const clearStayCheck = () => {
            if (stayCheckTimeoutId) {
                clearTimeout(stayCheckTimeoutId);
                stayCheckTimeoutId = null;
            }
        };

        const scheduleStayCheck = () => {
            // Clear any existing timeout
            clearStayCheck();

            // Schedule a check - if page is still here after delay, user clicked "Stay"
            // Using 500ms to give browser time to fully unload if user clicked "Reload"
            // The unload event will clear this timeout if page is actually unloading
            stayCheckTimeoutId = setTimeout(() => {
                // If we're still here after the timeout, user clicked "Stay"
                // Don't check visibilityState as it may not be updated yet in some browsers
                // When user clicks "Stay", we don't show the custom dialog because the deal was
                // already auto-saved in the beforeunload handler
                if (isPageUnloadingRef.current) {
                    isPageUnloadingRef.current = false;
                }
                stayCheckTimeoutId = null;
            }, 500);
        };

        const handleVisibilityChange = () => {
            // If page becomes visible and we were in unloading state, schedule check
            if (document.visibilityState === 'visible' && isPageUnloadingRef.current) {
                scheduleStayCheck();
            }
        };

        const handleFocus = () => {
            if (isPageUnloadingRef.current) {
                scheduleStayCheck();
            }
        };

        // If page is actually unloading (user clicked Reload), clear the timeout
        // Using 'unload' instead of 'pagehide' as pagehide fires too early in some browsers
        const handleUnload = () => {
            clearStayCheck();
            isPageUnloadingRef.current = false;
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);
        window.addEventListener('unload', handleUnload);

        return () => {
            clearStayCheck();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('unload', handleUnload);
        };
    }, [store]);
    useEffect(() => {
        addEventListener(BEFORE_UNLOAD_EVENT, beforeUnloadHandler);
        return () => {
            removeEventListener(BEFORE_UNLOAD_EVENT, beforeUnloadHandler);
        };
    }, []);

    useEffect(() => {
        if (hasForceReloadOnWSEventTriggered) {
            // Show reload modal when force reload is triggered (similar to UpdatedDealBanner pattern)
            // Only show if modal is not already showing
            if (!showReloadModalRef.current) {
                setReloadModal({
                    show: true,
                    // Falls back to a generic message if the trigger didn't carry a source/message
                    // (e.g. an older dispatch of setForceReloadOnWSEvent that only passed the boolean).
                    source: forceReloadSource || 'Unsaved Changes',
                    message:
                        forceReloadMessage || 'You have unsaved changes. Please reload to continue with the latest deal version.'
                });
                // Set ref BEFORE removing beforeunload listener so the handler can check it
                // This guards against race conditions where beforeunload fires during cleanup
                showReloadModalRef.current = true;
            }
            // Remove beforeunload listener AFTER setting the ref
            // This ensures beforeUnloadHandler can check showReloadModalRef if it fires during cleanup
            removeEventListener(BEFORE_UNLOAD_EVENT, beforeUnloadHandler);
            setShowUnsavedChangesDialog(false);
        }
    }, [hasForceReloadOnWSEventTriggered, forceReloadSource, forceReloadMessage]);

    // Show dialog when pending navigation is set (only if force reload is not triggered)
    useEffect(() => {
        if (pendingNavigation && !hasForceReloadOnWSEventTriggered) {
            setShowUnsavedChangesDialog(true);
        }
    }, [pendingNavigation, hasForceReloadOnWSEventTriggered]);

    return (
        <>
            {reloadModal.show ? (
                <ReloadModal reloadMessage={reloadModal.message} reloadSource={reloadModal.source} />
            ) : (
                showUnsavedChangesDialog &&
                !isPageUnloadingRef.current && (
                    <UnsavedChangesDialog
                        onHide={handleHide}
                        onDraftSuccess={toggleDraftSuccessSnackbar}
                        onUpdateSuccess={toggleUpdateSuccessSnackbar}
                        onLeaveWithoutSaving={handleLeaveWithoutSaving}
                    />
                )
            )}
            <Snackbar
                data-testid="update-offer-success-unsaved-changes"
                show={showUpdateSuccessSnackbar}
                message="We've updated your changes to this offer."
                position="bottom-center"
                onClose={toggleUpdateSuccessSnackbar}
                type="success"
            />
            <Snackbar
                data-testid="update-draft-success-unsaved-changes"
                show={showDraftSuccessSnackbar}
                message="Successfully Saved as Draft!"
                position="bottom-center"
                onClose={toggleDraftSuccessSnackbar}
                type="success"
            />
        </>
    );
};

export default UnsavedChangesManager;
